import React, { FC, useState } from 'react';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import Styles from '@/assets/js/Styles';

export interface ImportedPagesButtonProps {
  /** The imported view is the one showing; the button then reads "back to ..." and a click leaves it. */
  active: boolean;
  /** Picks the "back to events" wording over "back to the page tree". */
  isEvent?: boolean;
  onOpenHere: () => void;
  onOpenInNewWindow: () => void;
  onClose: () => void;
}

/**
 * Entry to the view of imported pages that still await publishing. The caller renders it only while such pages
 * exist. Outside the imported view the button offers to open it in this window or a new one.
 */
export const ImportedPagesButton: FC<ImportedPagesButtonProps> = ({
  active,
  isEvent = false,
  onOpenHere,
  onOpenInNewWindow,
  onClose,
}) => {
  const { t: tComponents } = useTranslation('components');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <Button
        variant='outlined'
        color={active ? 'primary' : 'secondary'}
        aria-haspopup={active ? undefined : 'menu'}
        aria-expanded={anchorEl ? true : undefined}
        sx={{
          px: 3,
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
          backgroundColor: active ? Styles.Colors.BLACK : Styles.Colors.LIGHT_GREY,
          '&:hover': {
            backgroundColor: active ? Styles.Colors.DARK_GREY : Styles.Colors.LIGHTEST_GREY,
          },
          border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
        }}
        onClick={(event) => (active ? onClose() : setAnchorEl(event.currentTarget))}
        startIcon={<MaterialSymbol name={active ? 'arrow_back' : 'input'} />}>
        {tComponents(
          active
            ? `ContentView.${isEvent ? 'backToEventsButtonLabel' : 'backToPageTreeButtonLabel'}`
            : 'ContentView.importedPagesButtonLabel',
        )}
      </Button>
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            closeMenu();
            onOpenHere();
          }}>
          <ListItemIcon>
            <MaterialSymbol name='open_in_browser' />
          </ListItemIcon>
          <ListItemText>{tComponents('ContentView.openImportedPagesHere')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            onOpenInNewWindow();
          }}>
          <ListItemIcon>
            <MaterialSymbol name='open_in_new' />
          </ListItemIcon>
          <ListItemText>{tComponents('ContentView.openImportedPagesInNewWindow')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ImportedPagesButton;
